import React, { useEffect, useRef, useState } from 'react';
import { HandHeart, Check, CircleCheck, BookOpen, Search, Footprints, PenLine } from 'lucide-react';
import { ShareButtons } from './ShareButtons';

const WORDS = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen'];
export const dayWord = (n) => (WORDS[n] || String(n)).toUpperCase();
const fmtDate = (iso) => iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }) : '';

export const Label = ({ children, className = '' }) => (
  <p className={`text-[11px] font-bold uppercase tracking-[0.3em] text-[#b8923a] ${className}`}>{children}</p>
);

const Paragraphs = ({ text, className = '' }) =>
  (text || '').split(/\n{2,}|\n/).filter((p) => p.trim()).map((p, i) => (
    <p key={i} className={`whitespace-pre-line ${className}`}>{p.trim()}</p>
  ));

const Notes = ({ storageKey }) => {
  const [value, setValue] = useState(() => { try { return localStorage.getItem(storageKey) || ''; } catch { return ''; } });
  return (
    <div className="mt-8">
      <Label className="mb-2 flex items-center gap-2 text-slate-400"><PenLine className="w-3.5 h-3.5" /> Notes</Label>
      <textarea
        value={value}
        onChange={(e) => { setValue(e.target.value); try { localStorage.setItem(storageKey, e.target.value); } catch { /* private mode */ } }}
        placeholder="Write it down. You'll want to read this back a year from now."
        rows={3}
        className="w-full bg-transparent border-0 border-b border-dashed border-slate-300 focus:border-[#1f4d36] focus:ring-0 text-slate-700 placeholder:text-slate-400 font-serif leading-8 resize-y px-0"
        style={{ backgroundImage: 'repeating-linear-gradient(transparent, transparent 31px, #e2e8f0 31px, #e2e8f0 32px)', lineHeight: '32px' }}
        data-testid={`devotional-notes-${storageKey.split(':').pop()}`}
      />
    </div>
  );
};

export const DevotionalDayCard = ({ day, slug, isToday, isFocus, shareUrl, shareImageUrl, devotionalTitle, isRead, onRead }) => {
  const ref = useRef(null);
  const readings = day.readings?.length ? day.readings : (day.scripture || day.scripture_text ? [{ text: day.scripture_text, reference: day.scripture }] : []);

  useEffect(() => {
    if (!onRead || isRead || !ref.current || typeof IntersectionObserver === 'undefined') return undefined;
    let timer = null;
    const io = new IntersectionObserver(([entry]) => {
      const vh = window.innerHeight || 800;
      const reading = entry.isIntersecting && (entry.intersectionRect.height >= 0.5 * vh || entry.intersectionRatio >= 0.85);
      if (reading) timer = timer || setTimeout(() => onRead(day.day_number), 8000);
      else if (timer) { clearTimeout(timer); timer = null; }
    }, { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.85, 1] });
    io.observe(ref.current);
    return () => { io.disconnect(); if (timer) clearTimeout(timer); };
  }, [onRead, isRead, day.day_number]);

  return (
    <article
      ref={ref}
      id={`day-${day.day_number}`}
      className={`scroll-mt-28 bg-white rounded-sm border border-slate-200/80 px-6 py-10 sm:px-12 sm:py-14 shadow-[0_1px_0_#e2e8f0] transition-shadow ${isFocus ? 'ring-1 ring-[#1f4d36]/40 shadow-xl shadow-green-900/5' : ''}`}
      data-testid={`devotional-day-${day.day_number}`}
    >
      <header>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Label>Day {dayWord(day.day_number)}</Label>
          {day.date && <span className="text-[11px] text-slate-400 tracking-wide">{fmtDate(day.date)}</span>}
          {isToday && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#1f4d36] text-white tracking-wide" data-testid={`devotional-today-badge-${day.day_number}`}>TODAY</span>}
          {isRead && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-50 text-[#1f4d36] inline-flex items-center gap-1" data-testid={`devotional-read-badge-${day.day_number}`}><Check className="w-3 h-3" /> READ</span>}
        </div>
        <h2 className="font-serif font-bold text-3xl sm:text-4xl text-[#1f4d36] leading-tight mt-3">{day.title}</h2>
        {day.subtitle && <p className="font-serif italic text-slate-500 mt-2 text-base md:text-lg">{day.subtitle}</p>}
      </header>

      {readings.length > 0 && (
        <section className="mt-8">
          <Label className="mb-3">Read</Label>
          <div className="bg-[#f7f4ea] border-l-[3px] border-[#b8923a] px-6 py-5 space-y-5">
            {readings.map((r, i) => (
              <div key={i}>
                {r.text && <p className="font-serif text-slate-800 text-base md:text-lg leading-relaxed whitespace-pre-line">{r.text}</p>}
                {r.reference && <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#1f4d36] mt-2">{r.reference}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {day.reflection && (
        <section className="mt-9">
          <Label className="mb-3">Reflect</Label>
          <div className="text-slate-700 leading-relaxed space-y-4 text-base"><Paragraphs text={day.reflection} /></div>
        </section>
      )}

      {day.digging_deeper && (
        <section className="mt-9 border border-[#1f4d36]/25 rounded-sm px-6 py-5 bg-[#f4f7f4]">
          <Label className="mb-3 flex items-center gap-2"><BookOpen className="w-3.5 h-3.5" /> Digging Deeper</Label>
          <div className="text-slate-700 text-sm md:text-base leading-relaxed space-y-3"><Paragraphs text={day.digging_deeper} /></div>
        </section>
      )}

      {day.questions?.length > 0 && (
        <section className="mt-9">
          <Label className="mb-3 flex items-center gap-2"><Search className="w-3.5 h-3.5" /> Search Your Heart</Label>
          <ol className="space-y-3">
            {day.questions.map((q, i) => (
              <li key={i} className="flex gap-4 text-slate-700 leading-relaxed">
                <span className="font-serif font-bold text-[#b8923a] text-lg leading-6 w-5 shrink-0">{i + 1}.</span>
                <span>{q}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {day.take_it_with_you && (
        <section className="mt-9">
          <Label className="mb-3 flex items-center gap-2"><Footprints className="w-3.5 h-3.5" /> Take It With You</Label>
          <div className="text-slate-700 leading-relaxed space-y-3"><Paragraphs text={day.take_it_with_you} /></div>
        </section>
      )}

      {day.prayer && (
        <section className="mt-9">
          <Label className="mb-3 flex items-center gap-2"><HandHeart className="w-3.5 h-3.5" /> Pray</Label>
          <div className="font-serif italic text-slate-800 text-base md:text-lg leading-relaxed space-y-3 border-l-[3px] border-slate-200 pl-5"><Paragraphs text={day.prayer} /></div>
        </section>
      )}

      {slug && <Notes storageKey={`lc_devotional_notes:${slug}:${day.day_number}`} />}

      {onRead && (
        <div className="mt-8 flex justify-end">
          <button
            type="button"
            onClick={() => onRead(day.day_number)}
            disabled={isRead}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${isRead ? 'bg-green-50 text-[#1f4d36] cursor-default' : 'bg-[#1f4d36] text-white hover:bg-[#173b29]'}`}
            data-testid={`devotional-mark-read-${day.day_number}`}
          >
            <CircleCheck className="w-4 h-4" /> {isRead ? 'Read' : 'Mark as read'}
          </button>
        </div>
      )}

      {shareUrl && (
        <footer className="mt-6 pt-4 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center gap-2">
          <span className="text-xs uppercase tracking-wider font-semibold text-slate-500 mr-1 shrink-0 whitespace-nowrap">Share Day {day.day_number}</span>
          <ShareButtons compact url={shareUrl} imageUrl={shareImageUrl} title={`${devotionalTitle ? `${devotionalTitle} · ` : ''}Day ${day.day_number}: ${day.title}`} text={day.scripture} testId={`share-day-${day.day_number}`} />
        </footer>
      )}
    </article>
  );
};
